import { Button } from '@/components/ui/button';
import {
  MultiStepForm,
  type StepConfig,
} from '@/components/ui/multi-step-form';
import { mintStockClips } from '@/features/socials/api/stock-clips';
import {
  useCreateVideo,
  useQueueVideoExport,
  useUpdateVideo,
} from '@/features/videos';
import { parseScriptToTextFrames } from '@/features/videos/lib/parse-script-to-text-frames';
import { logError } from '@/lib/log-error';
import { ROUTES } from '@/lib/route-paths';
import { useResolvedRoutes } from '@/lib/use-routes';
import { useUnsavedWorkGuard } from '@/lib/use-unsaved-work-guard';
import type { VideoDraftConfig } from '@borradh-workspace/api-client/types';
import { isV2VideoTemplate } from '@borradh-workspace/labels';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { z } from 'zod';
import { useVideoCreation } from '../-context';
import {
  type ClipSlots,
  type VideoFormData,
  captionReviewStepSchema,
  createBrandedVideoFormDefaults,
  createMediaStepSchema,
  customizationOnlyStepSchema,
  mapFontFamilyToCaptionFont,
  offerStepSchema,
  scriptStepSchema,
  serviceStepSchema,
  videoFormSchema,
} from '../-schema';
import type { SlotConfig } from '../data/-slot-config';
import { ProcessingModal } from './shared/processing-modal';
import {
  CaptionReviewStep,
  CustomiseStep,
  MediaSelectionStep,
  OfferStep,
  ScriptStep,
  ServiceStep,
} from './steps';

/**
 * Transform clip slots to bRollClips array with clipType.
 *
 * For templates with ordered slots (templateSlots), each slot is emitted
 * in its order with the slot's clipType from the slot config.
 *
 * For legacy/default templates, falls back to before → bRoll → after ordering.
 */
function buildBRollClips(
  clips: ClipSlots,
  slotConfigs: SlotConfig[]
): Array<{
  assetId: string;
  url: string;
  order: number;
  clipType: 'before' | 'after' | 'bRoll';
}> {
  const result: Array<{
    assetId: string;
    url: string;
    order: number;
    clipType: 'before' | 'after' | 'bRoll';
  }> = [];

  const hasOrdered = slotConfigs.some((s) => s.order !== undefined);

  if (hasOrdered && clips.templateSlots) {
    // Ordered template slots — emit in order (each slot may hold multiple clips)
    const sortedSlots = [...slotConfigs]
      .filter((s) => s.order !== undefined)
      .sort((a, b) => (a.order as number) - (b.order as number));

    for (const slot of sortedSlots) {
      const slotValue = clips.templateSlots[String(slot.order as number)];
      // Support both array (new) and single string (legacy) formats
      const assetIds = Array.isArray(slotValue)
        ? slotValue
        : slotValue
          ? [slotValue]
          : [];
      for (const assetId of assetIds) {
        result.push({
          assetId,
          url: '', // Populated by the video-worker from asset table
          order: result.length,
          clipType: slot.type,
        });
      }
    }
  } else {
    // Legacy fallback: before → bRoll → after
    if (clips.before) {
      result.push({
        assetId: clips.before,
        url: '',
        order: result.length,
        clipType: 'before',
      });
    }

    for (const assetId of clips.bRoll || []) {
      result.push({
        assetId,
        url: '',
        order: result.length,
        clipType: 'bRoll',
      });
    }

    if (clips.after) {
      result.push({
        assetId: clips.after,
        url: '',
        order: result.length,
        clipType: 'after',
      });
    }
  }

  return result;
}

/** Non-configurable caption defaults (not exposed in the form) */
const CAPTION_DEFAULTS = {
  fontSize: 64,
  highlightColor: '#FFFFFF',
  showBackground: false,
} as const;

/** Non-configurable outro defaults */
const OUTRO_DEFAULTS = {
  ctaText: 'Book Now',
  backgroundOpacity: 0.7,
  textColor: '#FFFFFF',
  durationSec: 3,
} as const;

interface VideoCreationFormProps {
  onStepChange?: (stepIndex: number, stepId: string) => void;
  adsReturnContext?: { campaignId: string };
  initialAllowStockFootage?: boolean;
  layout?: 'default' | 'mobile';
  onMobileStepIndexChange?: (stepIndex: number) => void;
  registerGoBack?: (goBack: (() => void) | null) => void;
}

export function VideoCreationForm({
  onStepChange,
  adsReturnContext,
  initialAllowStockFootage = false,
  layout = 'default',
  onMobileStepIndexChange,
  registerGoBack,
}: VideoCreationFormProps) {
  const navigate = useNavigate();
  const routes = useResolvedRoutes();
  const queryClient = useQueryClient();

  const [isProcessing, setIsProcessing] = useState(false);
  const [videoId, setVideoId] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const {
    talkingHeadUrl,
    isUploading,
    brand,
    videoDefaults,
    organizationName,
    selectedVariation,
    draftVideoId,
    setDraftVideoId,
    narrationMode,
    slotConfigs,
    startScriptGeneration,
    generatedScript,
    isGeneratingScript,
    hasMobileUpload,
    templateId,
  } = useVideoCreation();
  const { createVideoAsync, isCreating } = useCreateVideo();
  const { updateVideoAsync } = useUpdateVideo({ silent: true });
  const { queueExportAsync } = useQueueVideoExport();

  const isOffer = templateId === 'offer';
  const isEducational = templateId === 'educational';
  const isBeforeAfter = templateId === 'before-after';
  const isAuthoredV2 = isV2VideoTemplate(templateId);
  const requiresGeneratedScript = isEducational || isBeforeAfter;

  // These templates skip ScriptStep, so there is no editable fallback if AI
  // generation fails. Keep the service step retryable until copy is ready.
  const prepareScript = useCallback(
    async (serviceId?: string) => {
      const generation = startScriptGeneration(serviceId);
      if (!requiresGeneratedScript) {
        void generation;
        return true;
      }
      if (await generation) return true;
      toast.error(
        'We could not prepare your video text. Please try Continue again.'
      );
      return false;
    },
    [requiresGeneratedScript, startScriptGeneration]
  );

  // Create branded default values based on organization's content style
  const brandedDefaults = useMemo(() => {
    const defaults = createBrandedVideoFormDefaults(brand, videoDefaults);
    // Educational, before-after, and offer templates use text_only narration — set it as default
    // so schema refinements that check narrationType pass correctly
    if (isOffer || isEducational || isBeforeAfter || isAuthoredV2) {
      defaults.narrationType = 'text_only';
    }
    if (!isBeforeAfter) {
      defaults.allowStockFootage = initialAllowStockFootage;
    }
    // Leave music unpinned until the owner chooses it. The server resolves
    // the default against the current flag, including flags loaded later.

    return defaults;
  }, [
    brand,
    videoDefaults,
    isOffer,
    isEducational,
    isBeforeAfter,
    isAuthoredV2,
    initialAllowStockFootage,
  ]);

  // Warn before leaving with unsaved progress, and hold off a new build.
  useUnsavedWorkGuard(
    (!!draftVideoId || !!talkingHeadUrl || hasMobileUpload) && !isProcessing
  );

  /**
   * Save after selecting the service and, for offer templates, the offer.
   * Other templates save on the service step so recording has a draft QR code.
   */
  const handleDraftBeforeContinue = useCallback(
    async (
      form: import('react-hook-form').UseFormReturn<VideoFormData>
    ): Promise<boolean> => {
      const serviceId = form.getValues('serviceId') || undefined;
      const offerId = form.getValues('offerId') || undefined;

      if (draftVideoId) {
        // Draft already exists (user went back) — update serviceId + offerId
        try {
          await updateVideoAsync({
            id: draftVideoId,
            serviceId: serviceId || null,
            offerId: offerId || null,
          });
        } catch {
          // Non-critical
        }
        // Re-trigger script generation with the (possibly changed) service
        return prepareScript(serviceId);
      }

      try {
        const videoTitle = `Video ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

        const minimalDraftConfig: VideoDraftConfig = {
          scriptText: '',
          narrationType: narrationMode,
          bRollClips: [],
          captions: {
            enabled: true,
            position: videoDefaults?.captionPosition ?? 'bottom',
            fontFamily: videoDefaults?.captionFont
              ? mapFontFamilyToCaptionFont(videoDefaults.captionFont)
              : 'inter',
            textColor: videoDefaults?.captionColor ?? '#FFFFFF',
            backgroundColor: '#000000',
            ...CAPTION_DEFAULTS,
          },
          musicVolume: videoDefaults?.musicVolume ?? 0.05,
          outro: {
            businessName: organizationName || 'Business',
            backgroundColor: '#000000',
            ...OUTRO_DEFAULTS,
          },
          orientation: isOffer ? 'square' : 'portrait',
        };

        const video = await createVideoAsync({
          title: videoTitle,
          templateId,
          variationId: selectedVariation?.id,
          serviceId,
          offerId,
          draftConfig: minimalDraftConfig,
        });

        setDraftVideoId(video.id);
        // Start script generation with the selected service
        return prepareScript(serviceId);
      } catch (error) {
        logError('video.createDraft', error, { extra: { templateId } });
        toast.error('Failed to save. Please try again.');
        return false;
      }
    },
    [
      draftVideoId,
      updateVideoAsync,
      createVideoAsync,
      organizationName,
      templateId,
      selectedVariation?.id,
      setDraftVideoId,
      narrationMode,
      prepareScript,
      isOffer,
      videoDefaults,
    ]
  );

  /**
   * Called when the user clicks "Continue" on the script step.
   * Saves the script and narration settings to the existing draft.
   */
  const handleScriptBeforeContinue = useCallback(
    async (
      form: import('react-hook-form').UseFormReturn<VideoFormData>
    ): Promise<boolean> => {
      if (draftVideoId) {
        const currentNarrationType = form.getValues('narrationType');
        const isAi = currentNarrationType === 'ai_voiceover';
        const isTextOnly = currentNarrationType === 'text_only';
        try {
          await updateVideoAsync({
            id: draftVideoId,
            draftConfig: {
              scriptText: form.getValues('scriptText'),
              narrationType: currentNarrationType,
              // Send null for fields that should be cleared when switching narration type
              aiVoiceId: isAi ? form.getValues('aiVoiceId') : null,
              talkingHeadUrl:
                isAi || isTextOnly ? null : talkingHeadUrl || undefined,
              // Save text frames for text_only mode
              textFrames: isTextOnly ? form.getValues('textFrames') : undefined,
            },
          });
        } catch {
          // Non-critical — auto-save will catch up
        }
      }
      return true;
    },
    [draftVideoId, updateVideoAsync, talkingHeadUrl]
  );

  /**
   * Called when the user clicks "Continue" on the caption review step.
   * Saves the edited caption text to the draft config.
   */
  const handleCaptionReviewBeforeContinue = useCallback(
    async (
      form: import('react-hook-form').UseFormReturn<VideoFormData>
    ): Promise<boolean> => {
      if (draftVideoId) {
        try {
          await updateVideoAsync({
            id: draftVideoId,
            draftConfig: {
              editedCaptionText: form.getValues('editedCaptionText') || null,
            },
          });
        } catch {
          // Non-critical — auto-save will catch up
        }
      }
      return true;
    },
    [draftVideoId, updateVideoAsync]
  );

  // Steps definition — offer and educational templates skip script step
  const steps: StepConfig<VideoFormData>[] = useMemo(() => {
    const baseSteps: StepConfig<VideoFormData>[] = [
      {
        id: 'service',
        schema: serviceStepSchema,
        component: (
          form: import('react-hook-form').UseFormReturn<VideoFormData>
        ) => <ServiceStep form={form} />,
        // Offer drafts require the offer chosen on the next step.
        onBeforeContinue:
          isOffer || isAuthoredV2 ? undefined : handleDraftBeforeContinue,
        processingButtonText: requiresGeneratedScript
          ? 'Preparing video text...'
          : 'Saving...',
      },
    ];

    if (isOffer) {
      baseSteps.push({
        id: 'offer',
        schema: offerStepSchema,
        component: (
          form: import('react-hook-form').UseFormReturn<VideoFormData>
        ) => <OfferStep form={form} />,
        onBeforeContinue: handleDraftBeforeContinue,
        processingButtonText: 'Saving...',
      });
    } else if (!isEducational && !isBeforeAfter && !isAuthoredV2) {
      // Educational and before-after templates skip the script step — they use text_only with
      // AI-generated text frames and music, so no script editing or narration mode needed
      baseSteps.push({
        id: 'script',
        schema: scriptStepSchema,
        component: (
          form: import('react-hook-form').UseFormReturn<VideoFormData>
        ) => <ScriptStep form={form} />,
        onBeforeContinue: handleScriptBeforeContinue,
        processingButtonText: 'Saving...',
      });
    }

    // Only show caption review for recorded narration with a talking head
    if (
      !isOffer &&
      !isEducational &&
      !isBeforeAfter &&
      !isAuthoredV2 &&
      narrationMode === 'recorded' &&
      talkingHeadUrl
    ) {
      baseSteps.push({
        id: 'caption-review',
        schema: captionReviewStepSchema,
        component: (
          form: import('react-hook-form').UseFormReturn<VideoFormData>
        ) => <CaptionReviewStep form={form} />,
        onBeforeContinue: handleCaptionReviewBeforeContinue,
        processingButtonText: 'Saving...',
      });
    }

    // Dynamic media selection steps (one per slot)
    for (const slot of slotConfigs) {
      baseSteps.push({
        id: `media-${slot.order ?? slot.type}`,
        schema: createMediaStepSchema(slot, {
          allowStockFootageBypassesRequired: !isBeforeAfter,
        }),
        component: (
          form: import('react-hook-form').UseFormReturn<VideoFormData>
        ) => <MediaSelectionStep form={form} slot={slot} />,
      });
    }

    // Final customization step (music + captions only)
    baseSteps.push({
      id: 'customization',
      schema: customizationOnlyStepSchema,
      component: (
        form: import('react-hook-form').UseFormReturn<VideoFormData>
      ) => <CustomiseStep form={form} />,
    });

    return baseSteps;
  }, [
    handleDraftBeforeContinue,
    handleScriptBeforeContinue,
    handleCaptionReviewBeforeContinue,
    isOffer,
    isEducational,
    isBeforeAfter,
    isAuthoredV2,
    requiresGeneratedScript,
    narrationMode,
    talkingHeadUrl,
    slotConfigs,
  ]);

  // Determine if user can continue based on current step
  const canContinue = (() => {
    const currentStep = steps[currentStepIndex];
    // On the script step with recorded narration, require video upload
    // AI voiceover and text_only modes don't need a talking head
    if (currentStep?.id === 'script' && narrationMode === 'recorded') {
      return !!talkingHeadUrl && !isUploading;
    }
    return true;
  })();

  const handleSubmit = async (data: VideoFormData) => {
    if (
      requiresGeneratedScript &&
      (isGeneratingScript || !generatedScript?.trim())
    ) {
      toast.error(
        'Your video text is not ready. Go back to the service step and try Continue again.'
      );
      return;
    }
    try {
      setIsProcessing(true);

      // Auto-generate title if not provided
      const videoTitle =
        data.title?.trim() ||
        `Video ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

      const effectiveNarrationType =
        isOffer || isEducational || isBeforeAfter || isAuthoredV2
          ? 'text_only'
          : data.narrationType;
      const isAiVoiceover = effectiveNarrationType === 'ai_voiceover';
      const isTextOnly = effectiveNarrationType === 'text_only';

      // For educational and before-after templates, use the AI-generated script (the script step was skipped)
      const effectiveScriptText =
        isEducational || isBeforeAfter
          ? generatedScript || ''
          : data.scriptText;

      // Mint explicitly picked stock clips into org assets and append them
      // after uploaded b-roll. Hand-picking remains opt-in via the toggle.
      const uploadedBRollClips = buildBRollClips(data.clips, slotConfigs);
      let stockBRollClips: typeof uploadedBRollClips = [];
      if (data.allowStockFootage && data.stockClipIds.length > 0) {
        const minted = await mintStockClips(data.stockClipIds);
        stockBRollClips = data.stockClipIds
          .map((id) => minted[id])
          .filter((assetId): assetId is string => !!assetId)
          .map((assetId, i) => ({
            assetId,
            url: '',
            order: 10_000 + i,
            clipType: 'bRoll' as const,
          }));
      }
      const finalBRollClips = [...uploadedBRollClips, ...stockBRollClips];

      // Build the full VideoDraftConfig from form data + brand settings
      // Use null (not undefined) to explicitly clear fields during deep merge
      const draftConfig: VideoDraftConfig = {
        // Narration settings
        narrationType: effectiveNarrationType,
        aiVoiceId: isAiVoiceover ? data.aiVoiceId : null,

        // User-edited script (or AI-generated for educational)
        scriptText: isAuthoredV2 ? undefined : effectiveScriptText,

        // Talking head source (null clears stale value when switching to AI voiceover or text_only)
        talkingHeadUrl:
          isAiVoiceover || isTextOnly || !data.talkingHeadUrl
            ? null
            : data.talkingHeadUrl,

        // Text frames for text_only narration mode
        // Educational/before-after: auto-parse scriptText into styled text frames
        // Other text_only: use user-provided frames from form
        textFrames:
          isEducational || isBeforeAfter
            ? parseScriptToTextFrames(effectiveScriptText)
            : isTextOnly && !isOffer && !isAuthoredV2
              ? data.textFrames
              : undefined,

        // Offer card overlay for offer templates
        offerCard: isOffer
          ? {
              serviceName: data.offerHeadline || '',
              headline: data.offerHeadline || '',
              bulletPoints: data.bulletPoints?.filter(Boolean),
              ctaText: data.ctaText || 'Book now',
              urgencyText: data.urgencyText || undefined,
              audienceText: data.audienceText || undefined,
              logoUrl: brand?.logoUrl || undefined,
              businessName: organizationName || undefined,
              primaryColor: brand?.primaryColor || '#007AFF',
              secondaryColor: brand?.secondaryColor || '#FFFFFF',
            }
          : undefined,

        // B-roll clips from slot-based selection + any minted stock clips
        // (with clipType for ordering)
        bRollClips: finalBRollClips,

        // Caption settings from form + brand
        captions: {
          enabled: !isAuthoredV2 && data.captionsEnabled,
          position: data.position,
          fontFamily: data.fontFamily,
          textColor: data.textColor,
          backgroundColor: data.backgroundColor,
          ...CAPTION_DEFAULTS,
        },

        // Music settings
        musicTrackId: data.musicTrackId,
        musicUrl: data.musicUrl,
        musicVolume: data.musicVolume,

        // Outro overlay from brand settings
        outro: isAuthoredV2
          ? undefined
          : {
              logoUrl: brand?.logoUrl || undefined,
              businessName: organizationName || 'Your Business',
              backgroundColor: brand?.primaryColor || '#000000',
              outroStyle: data.outroStyle || undefined,
              ...OUTRO_DEFAULTS,
            },

        // User-edited caption text (overrides Whisper during render)
        editedCaptionText: data.editedCaptionText || null,

        // Square for offer templates, portrait for everything else
        orientation: isOffer ? 'square' : 'portrait',
      };

      // Safety net: auto-fill from stock at render time whenever the final
      // b-roll list is empty. The toggle only controls whether users browse
      // and hand-pick specific stock clips in the form.
      const allowStockFootage =
        data.allowStockFootage || finalBRollClips.length === 0;

      if (draftVideoId) {
        // Draft already exists — update it with the full config
        await updateVideoAsync({
          id: draftVideoId,
          title: videoTitle,
          serviceId: data.serviceId || null,
          offerId: data.offerId || null,
          draftConfig,
        });
        setVideoId(draftVideoId);
        await queueExportAsync({ videoId: draftVideoId, allowStockFootage });
      } else {
        // Fallback: create + export (shouldn't happen in normal flow)
        const video = await createVideoAsync({
          title: videoTitle,
          templateId,
          variationId: selectedVariation?.id,
          serviceId: data.serviceId || undefined,
          offerId: data.offerId || undefined,
          draftConfig,
        });
        setVideoId(video.id);
        await queueExportAsync({ videoId: video.id, allowStockFootage });
      }
    } catch (error) {
      logError('video.create', error, { extra: { templateId } });
      toast.error('Failed to create video. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleProcessingClose = () => {
    setIsProcessing(false);
    setVideoId(null);
  };

  const handleVideoReady = async () => {
    toast.success('Video created successfully!');
    if (adsReturnContext?.campaignId && videoId) {
      await queryClient.invalidateQueries({ queryKey: ['videos'] });
      await queryClient.invalidateQueries({ queryKey: ['video', videoId] });
      void navigate({
        to: ROUTES.adsNew,
        search: { campaignId: adsReturnContext.campaignId, videoId },
        replace: true,
      });
      return;
    }
    void navigate({ to: routes.contentGallery });
  };

  const handleStepChange = (stepIndex: number, stepId: string) => {
    setCurrentStepIndex(stepIndex);
    onStepChange?.(stepIndex, stepId);
    onMobileStepIndexChange?.(stepIndex);
  };

  if (!selectedVariation) {
    return (
      <div className="flex flex-col items-center gap-4 px-6 py-12 text-center">
        <h1 className="text-2xl font-semibold">
          This video format is no longer available
        </h1>
        <p className="max-w-md text-muted-foreground">
          Choose another format to create your video.
        </p>
        <Button
          onClick={() => {
            if (adsReturnContext?.campaignId) {
              void navigate({
                to: ROUTES.adsNewVideoFormat,
                search: { campaignId: adsReturnContext.campaignId },
              });
            } else {
              void navigate({ to: routes.contentGallery });
            }
          }}
        >
          Back to {adsReturnContext ? 'video formats' : 'content library'}
        </Button>
      </div>
    );
  }

  return (
    <>
      <MultiStepForm
        steps={steps}
        defaultValues={brandedDefaults}
        fullSchema={videoFormSchema as z.ZodSchema<VideoFormData>}
        onSubmit={handleSubmit}
        onStepChange={handleStepChange}
        submitButtonText={isCreating ? 'Creating...' : 'Create Video'}
        continueButtonText="Continue"
        canContinue={canContinue}
        layout={layout}
        registerGoBack={
          layout === 'mobile' && !isProcessing ? registerGoBack : undefined
        }
      />

      <ProcessingModal
        open={isProcessing}
        onClose={handleProcessingClose}
        videoId={videoId}
        onVideoReady={() => void handleVideoReady()}
        autoContinueOnReady={Boolean(adsReturnContext?.campaignId)}
        readyActionLabel={
          adsReturnContext ? 'Continue to ad' : 'View in Library'
        }
      />
    </>
  );
}
